import {
  apiRequest,
  createAntdFilter,
  Folder,
  MemberMarker,
  Topic,
  useAccount,
  useDatadog,
  useGlobal,
  VojomoLoader
} from "@vojomo-core/shared";
import {
  BottomSheet,
  TouchFAB
} from "@vojomo-core/shared/src/components/Mobile";
import { useEffect, useMemo, useState, useRef } from "react";
import Table, { ColumnsType } from "antd/es/table";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getFriendlyTimeAgo } from "utils/helpers/time";
import {
  Alert,
  Button,
  ConfigProvider,
  Dropdown,
  Input,
  Modal,
  TableProps,
  Breadcrumb,
  Select,
  message,
  Tooltip
} from "antd";
import {
  FaEllipsisVertical,
  FaFolder,
  FaFile,
  FaPlus,
  FaChevronDown,
  FaLock
} from "react-icons/fa6";

import { DeleteModal } from "./DeleteModal";
import { ShareAccessModal } from "pages/TopicViewer/components/ShareAccessModal";
import { useCreateNote, CreateNoteModal } from "components/CreateNote";
import React from "react";
import { ChevronDown, ChevronUp, FileText, Download, Maximize2, Trash2, ArrowLeft } from "lucide-react";
import { renderMarkdown } from "utils/renderMarkdown";
import { exportTarPdf } from "utils/tarPdfExport";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

interface SynthesisDoc {
  id: string;
  sessionId: string;
  sessionName: string;
  periodType?: "weekly" | "qbr";
  periodKey: string;
  reportMarkdown: string;
  reportType?: string;
  contributorCount?: number;
  contributorNames?: string[];
  generatedAt: string;
}

function formatTarPeriodLabel(periodType: string, periodKey: string): string {
  if (periodType === "weekly") {
    const m = periodKey.match(/^(\d{4})-W(\d{2})$/);
    if (m) {
      const year = parseInt(m[1]);
      const week = parseInt(m[2]);
      const jan4 = new Date(year, 0, 4);
      const iso = new Date(jan4.getTime() - ((jan4.getDay() + 6) % 7) * 86400000 + (week - 1) * 7 * 86400000);
      const sunday = new Date(iso.getTime() + 6 * 86400000);
      const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      return `Weekly — ${fmt(iso)}–${fmt(sunday)}, ${year}`;
    }
    return `Weekly — ${periodKey}`;
  }
  const m = periodKey.match(/^(\d{4})-Q(\d)$/);
  if (m) return `QBR — Q${m[2]} FY${m[1]}`;
  return `QBR — ${periodKey}`;
}

function TeamAlignmentReportsSection() {
  const [docs, setDocs] = React.useState<SynthesisDoc[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState(false);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [focusedDoc, setFocusedDoc] = React.useState<SynthesisDoc | null>(null);

  const handleDeleteReport = async (doc: SynthesisDoc) => {
    const rawId = doc.id.replace(/^nqo-/, "");
    const isNqo = doc.id.startsWith("nqo-");
    const url = isNqo
      ? `${SERVER_URL}/api/rb/sessions/${doc.sessionId}/alignment-reports/${rawId}`
      : `${SERVER_URL}/api/rb/sessions/${doc.sessionId}/progress-syntheses/${rawId}`;
    const res = await fetch(url, { method: "DELETE", credentials: "include" });
    if (res.ok) setDocs((prev) => prev.filter((d) => d.id !== doc.id));
  };

  React.useEffect(() => {
    Promise.all([
      fetch(`${SERVER_URL}/api/rb/progress-syntheses/all`, { credentials: "include" })
        .then((r) => r.ok ? r.json() : []),
      fetch(`${SERVER_URL}/api/rb/alignment-reports/all`, { credentials: "include" })
        .then((r) => r.ok ? r.json() : []),
    ])
      .then(([progressDocs, alignDocs]) => {
        // Convert NQO alignment reports to SynthesisDoc shape
        const nqoDocs: SynthesisDoc[] = (alignDocs as Array<{ id: string; sessionId: string; sessionName?: string; reportType?: string; periodKey?: string; reportMarkdown: string; generatedAt?: string; createdAt?: string }>)
          .filter((d) => d.reportType === "nqo_synthesis")
          .map((d) => ({
            id: `nqo-${d.id}`,
            sessionId: d.sessionId,
            sessionName: d.sessionName || "Unknown Session",
            reportType: "nqo_synthesis",
            periodKey: d.periodKey || "",
            reportMarkdown: d.reportMarkdown,
            generatedAt: d.generatedAt || d.createdAt || "",
          }));
        const all = [...(progressDocs as SynthesisDoc[]), ...nqoDocs]
          .sort((a, b) => new Date(b.generatedAt || 0).getTime() - new Date(a.generatedAt || 0).getTime());
        setDocs(all);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading || docs.length === 0) return null;

  if (focusedDoc) {
    const focusPeriodLabel = focusedDoc.reportType === "nqo_synthesis"
      ? (() => { const qm = focusedDoc.periodKey.match(/^(\d{4})-Q(\d)$/); return qm ? `Q${qm[2]} ${qm[1]}` : focusedDoc.periodKey; })()
      : formatTarPeriodLabel(focusedDoc.periodType || "qbr", focusedDoc.periodKey);
    const focusTitlePrefix = focusedDoc.reportType === "nqo_synthesis" ? "QO Team Alignment Report" : "Team Alignment Report";
    return (
      <div style={{ marginBottom: 16, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#faf8ff", borderBottom: "1px solid #e2e8f0" }}>
          <button
            onClick={() => setFocusedDoc(null)}
            style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", color: "#2c1561", fontSize: 12, fontWeight: 500, padding: 0 }}
          >
            <ArrowLeft style={{ width: 14, height: 14 }} />
            Back
          </button>
          <span style={{ flex: 1, fontWeight: 600, fontSize: 13, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {focusTitlePrefix} {focusPeriodLabel}
          </span>
          {focusedDoc.sessionName && (
            <span style={{ fontSize: 10, color: "#94a3b8" }}>{focusedDoc.sessionName}</span>
          )}
        </div>
        <div style={{ padding: "16px 20px", maxHeight: 600, overflowY: "auto" }}>
          {renderMarkdown(focusedDoc.reportMarkdown, { baseFontSize: 12, context: "studio" })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 16, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
      {/* Section header */}
      <button
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: "flex", alignItems: "center", gap: 8, width: "100%",
          padding: "10px 14px", background: "#faf8ff", border: "none", cursor: "pointer",
          textAlign: "left", borderBottom: expanded ? "1px solid #e2e8f0" : "none",
        }}
      >
        <FileText style={{ width: 14, height: 14, color: "#2c1561", flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: 13, color: "#2c1561", flex: 1 }}>
          Team Alignment Reports
        </span>
        <span style={{ fontSize: 11, color: "#94a3b8", background: "#f1f5f9", borderRadius: 999, padding: "2px 8px" }}>
          {docs.length}
        </span>
        {expanded
          ? <ChevronUp style={{ width: 13, height: 13, color: "#94a3b8" }} />
          : <ChevronDown style={{ width: 13, height: 13, color: "#94a3b8" }} />
        }
      </button>

      {expanded && (
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {docs.map((doc, i) => {
            const isExpanded = expandedId === doc.id;
            // Format label based on report type
            let periodLabel: string;
            let titlePrefix: string;
            if (doc.reportType === "nqo_synthesis") {
              const qm = doc.periodKey.match(/^(\d{4})-Q(\d)$/);
              periodLabel = qm ? `Q${qm[2]} ${qm[1]}` : doc.periodKey;
              titlePrefix = "QO Team Alignment Report";
            } else {
              periodLabel = formatTarPeriodLabel(doc.periodType || "qbr", doc.periodKey);
              titlePrefix = "Team Alignment Report";
            }
            const names = doc.contributorNames?.length
              ? doc.contributorNames.join(", ")
              : doc.contributorCount ? `${doc.contributorCount} contributor${doc.contributorCount !== 1 ? "s" : ""}` : "";
            const runAt = doc.generatedAt
              ? new Date(doc.generatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : "";
            return (
              <div key={doc.id} style={{ borderTop: i === 0 ? "none" : "1px solid #f1f5f9" }}>
                <button
                  onClick={() => setExpandedId(isExpanded ? null : doc.id)}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 10, width: "100%",
                    padding: "10px 14px", background: isExpanded ? "#faf8ff" : "#fff",
                    border: "none", cursor: "pointer", textAlign: "left",
                  }}
                >
                  <FileText style={{ width: 13, height: 13, color: "#2c1561", flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {titlePrefix} {periodLabel}
                    </div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                      {[doc.sessionName, names, runAt].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, marginTop: 2 }}>
                    <span
                      role="button"
                      title="Download as PDF"
                      onClick={(e) => {
                        e.stopPropagation();
                        exportTarPdf(doc.reportMarkdown, {
                          companyName: doc.sessionName || "Roadmap Session",
                          periodLabel: `${titlePrefix} ${periodLabel}`,
                          contributorNames: doc.contributorNames,
                          generatedAt: doc.generatedAt,
                          sessionName: doc.sessionName,
                        });
                      }}
                      style={{ color: "#cbd5e1", cursor: "pointer", display: "flex", alignItems: "center" }}
                      onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = "#2c1561")}
                      onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "#cbd5e1")}
                    >
                      <Download style={{ width: 12, height: 12 }} />
                    </span>
                    <span
                      role="button"
                      title="Focus mode"
                      onClick={(e) => { e.stopPropagation(); setFocusedDoc(doc); }}
                      style={{ color: "#cbd5e1", cursor: "pointer", display: "flex", alignItems: "center" }}
                      onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = "#2c1561")}
                      onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "#cbd5e1")}
                    >
                      <Maximize2 style={{ width: 12, height: 12 }} />
                    </span>
                    <span
                      role="button"
                      title="Delete report"
                      onClick={(e) => { e.stopPropagation(); handleDeleteReport(doc); }}
                      style={{ color: "#cbd5e1", cursor: "pointer", display: "flex", alignItems: "center" }}
                      onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.color = "#ef4444")}
                      onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.color = "#cbd5e1")}
                    >
                      <Trash2 style={{ width: 12, height: 12 }} />
                    </span>
                    {isExpanded
                      ? <ChevronUp style={{ width: 13, height: 13, color: "#94a3b8" }} />
                      : <ChevronDown style={{ width: 13, height: 13, color: "#94a3b8" }} />
                    }
                  </div>
                </button>
                {isExpanded && (
                  <div style={{ padding: "12px 14px 16px 38px", borderTop: "1px solid #f1f5f9", background: "#fff", maxHeight: 400, overflowY: "auto" }}>
                    {renderMarkdown(doc.reportMarkdown, { baseFontSize: 11, context: "studio" })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface DataType {
  _id: string;
  updatedAt: Date;
  title: string;
  displayName?: string;
  versions: string;
  type: string;
  fmtType?: string;
  isBuildingBlock?: boolean;
  isShared?: boolean;
  key: number | string;
  isFolder?: boolean;
  parentFolderId?: string;
  wip?: boolean;
  isLocked?: boolean;
  memberName?: string;
  owner?: string;
  ownerName?: string;
  isTeam?: boolean;
  sourceType?: "Vojomo Document" | "Upload";
  createdAt?: Date;
}

export const DocsListInternal = ({
  newTab = false,
  height = null,
  style = {},
  filteredId = null
}: {
  newTab?: boolean;
  height?: string;
  style?: React.CSSProperties;
  filteredId?: string;
}) => {
  const { windowWidth, windowHeight, isMobile } = useGlobal();
  const navigate = useNavigate();
  const { trackEvent, trackError } = useDatadog();
  const [searchParams, setSearchParams] = useSearchParams();
  const { updateImpersonatedCompany, vojomoAccount, memberAccount } =
    useAccount();

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isRenameModalVisible, setIsRenameModalVisible] = useState(false);
  const [newTopicName, setNewTopicName] = useState("");
  const [selectedTopic, setSelectedTopic] = useState<DataType>(null);
  const [isProcessingCommand, setIsProcessingCommand] = useState(false);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderPath, setFolderPath] = useState<
    Array<{ _id: string; name: string }>
  >([]);
  const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] =
    useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
  const [isBulkMoveModalVisible, setIsBulkMoveModalVisible] = useState(false);
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);
  const [allFolders, setAllFolders] = useState<Folder[]>([]);
  const [selectedDestinationFolder, setSelectedDestinationFolder] = useState<
    string | null
  >(null);
  const [isCurrentFolderLocked, setIsCurrentFolderLocked] = useState(false);
  const [rowData, setRowData] = useState<Array<DataType> | null>(null);

  // Mobile State
  const [mobileActionSheet, setMobileActionSheet] = useState(false);
  const [mobileActionItem, setMobileActionItem] = useState<DataType | null>(
    null
  );
  const [mobileCreateSheet, setMobileCreateSheet] = useState(false);

  // Upload State
  const [gcsPath, setGcsPath] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sameNameModalOpen, setSameNameModalOpen] = useState(false);
  const [sameHashModalOpen, setSameHashModalOpen] = useState(false);
  const [sameHashModalFiles, setSameHashModalFiles] = useState<any[]>([]);
  const [ignoreSameHash, setIgnoreSameHash] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [newFileName, setNewFileName] = useState<string | null>(null);
  const [isNewFileNameTaken, setIsNewFileNameTaken] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);

  // File Preview Modal State
  const [isFilePreviewModalVisible, setIsFilePreviewModalVisible] =
    useState(false);
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [selectedFileType, setSelectedFileType] = useState<string | null>(null);
  const [isLoadingFileUrl, setIsLoadingFileUrl] = useState(false);

  // Ref for the debounce timeout
  const checkFileNameTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Create note hook
  const createNoteHook = useCreateNote({
    onNoteCreated: async () => {
      await getDocs(currentFolderId);
    }
  });

  // Helper function to get file extension
  const getFileExtension = (filename: string): string => {
    const parts = filename.split(".");
    return parts.length > 1 ? `.${parts[parts.length - 1]}` : "";
  };

  // Helper function to ensure filename has the correct extension
  const ensureFileExtension = (
    newName: string,
    originalName: string
  ): string => {
    const originalExt = getFileExtension(originalName);
    // If new name already has the correct extension, return as is
    if (newName.endsWith(originalExt)) {
      return newName;
    }

    // Remove any extension from new name and add the original extension
    const newNameWithoutExt = newName.includes(".")
      ? newName.substring(0, newName.lastIndexOf("."))
      : newName;

    return `${newNameWithoutExt}${originalExt}`;
  };

  async function getBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
    });
  }

  // Check if a filename already exists in the database
  const checkFileNameExists = async (fileName: string): Promise<boolean> => {
    try {
      const check_file_name = await apiRequest<any>({
        subRoute: "ai",
        path: `/check_file_name?file_name=${encodeURIComponent(fileName)}`,
        method: "GET"
      });

      return check_file_name.length > 0;
    } catch (error) {
      trackError("error_checking_file_name", error);
      return false; // Assume it doesn't exist on error to allow user to try upload
    }
  };

  const handleRenameAndUpload = async () => {
    if (!file || !newFileName) {
      setErrorMessage("File or new name is missing");
      return;
    }

    const correctedFileName = ensureFileExtension(newFileName, file.name);

    setIsLoading(true);

    try {
      // Check if the new filename already exists
      const fileNameExists = await checkFileNameExists(correctedFileName);

      if (fileNameExists) {
        setIsNewFileNameTaken(true);
        setErrorMessage(
          `The name "${correctedFileName}" is already taken. Please choose another name.`
        );
        setIsLoading(false);
        return;
      }

      // Create a new file with the new name (with correct extension) but same content
      const renamedFile = new File([file], correctedFileName, {
        type: file.type
      });
      const base64_file = await getBase64(renamedFile);

      const data = await apiRequest<{
        gcs_path: string;
        inserted_id: string;
      }>({
        subRoute: "ai",
        path: `/upload_file`,
        method: "POST",
        body: {
          base64_file: base64_file,
          file_type: renamedFile.type,
          file_name: renamedFile.name
        }
      });

      setGcsPath(data.gcs_path);
      setSameNameModalOpen(false);
      setNewFileName(null);
      setIsNewFileNameTaken(false);
      getDocs(currentFolderId);
    } catch (error) {
      trackError("error_uploading_renamed_file", error);
      setErrorMessage("Error uploading renamed file");
    } finally {
      setIsLoading(false);
    }
  };

  // Extract common file processing logic into a reusable function
  const processFileUpload = async (
    fileToProcess: File,
    skipHashCheck: boolean = false
  ) => {
    setErrorMessage(null);

    if (fileToProcess.size > 10 * 1024 * 1024) {
      setErrorMessage("File is too large. Maximum size is 10MB.");
      return false;
    }

    if (
      ![
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain"
      ].includes(fileToProcess.type)
    ) {
      setErrorMessage(
        "Invalid file type. Only PDF, DOC, DOCX, and TXT files are supported."
      );
      return false;
    }

    try {
      const base64_file: string = await getBase64(fileToProcess);
      setIsLoading(true);

      // Check file hash if not skipping hash check
      if (!skipHashCheck) {
        const check_file_hash = await apiRequest<any>({
          subRoute: "ai",
          path: `/check_file_hash`,
          method: "POST",
          body: {
            base64_file: base64_file
          }
        });

        if (check_file_hash.length > 0) {
          trackEvent("same_hash_file_upload", {
            hash: check_file_hash,
            fileName: fileToProcess.name,
            fileSize: fileToProcess.size,
            fileType: fileToProcess.type
          });
          setSameHashModalFiles(check_file_hash);
          setSameHashModalOpen(true);
          setIsLoading(false);
          return false;
        }
      }

      // Check if file name already exists
      const check_file_name = await apiRequest<any>({
        subRoute: "ai",
        path: `/check_file_name?file_name=${encodeURIComponent(fileToProcess.name)}`,
        method: "GET"
      });

      if (check_file_name.length > 0) {
        trackEvent("same_name_file_upload", {
          fileName: fileToProcess.name,
          fileType: fileToProcess.type,
          fileSize: fileToProcess.size
        });
        setSameNameModalOpen(true);
        setNewFileName(fileToProcess.name); // Initialize with current name for editing
        setIsLoading(false);
        return false;
      }

      // Upload the file
      const data = await apiRequest<{
        gcs_path: string;
        inserted_id: string;
      }>({
        subRoute: "ai",
        path: `/upload_file`,
        method: "POST",
        body: {
          base64_file: base64_file,
          file_type: fileToProcess.type,
          file_name: fileToProcess.name
        }
      });

      setGcsPath(data.gcs_path);
      getDocs(currentFolderId); // Refresh the list after successful upload
      return true;
    } catch (error) {
      trackError("error_during_file_upload", error);
      setErrorMessage("Failed to upload file. Please try again.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = async (e: any) => {
    // Check if there are files selected
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }

    let selectedFile = e.target.files[0] as File;
    setFile(selectedFile);

    try {
      await processFileUpload(selectedFile, ignoreSameHash);
    } finally {
      setIgnoreSameHash(false);
      // Reset the file input
      const fileInput = document.getElementById(
        "fileInput"
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";
    }
  };

  const handleFileClick = async (
    sourceId: string,
    fileName: string,
    fileType: string
  ) => {
    setIsLoadingFileUrl(true);
    setSelectedFileName(fileName);
    setSelectedFileType(fileType);
    setIsFilePreviewModalVisible(true);

    try {
      const response = await apiRequest<{
        url: string;
        file_type: string;
        file_name: string;
      }>({
        subRoute: "ai",
        path: `/get_source_url?source_id=${sourceId}`,
        method: "GET"
      });

      if (response?.url) {
        trackEvent("fetched_source", { response, fileName, fileType });
        setSelectedFileUrl(response.url);
      } else {
        trackError("error_no_url_in_response", null, response);
        message.error("Failed to load file preview - no URL returned");
        setIsFilePreviewModalVisible(false);
      }
    } catch (error: any) {
      trackError("error_fetching_file_url", error);

      const errorMsg =
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to load file preview";
      message.error(errorMsg);
      setIsFilePreviewModalVisible(false);
    } finally {
      setIsLoadingFileUrl(false);
    }
  };

  // Effect to auto-close alerts after 15 seconds
  useEffect(() => {
    if (errorMessage || gcsPath) {
      const timer = setTimeout(() => {
        if (errorMessage) setErrorMessage(null);
        if (gcsPath) {
          setGcsPath(null);
          getDocs(currentFolderId);
        }
      }, 15000); // 15 seconds

      return () => clearTimeout(timer);
    }
  }, [errorMessage, gcsPath]);

  const tableHeight = useMemo(() => {
    if (height === null) {
      const headerOffset = windowWidth < 1600 ? 285 : 265;
      const footerOffset = isMobile ? 72 : 64;
      return Math.max(200, windowHeight - headerOffset - footerOffset);
    }

    return `calc(${height} - 75px)`;
  }, [height, windowWidth, windowHeight, isMobile]);

  const handleDelete = async () => {
    if (!selectedTopic) {
      return;
    }

    setIsProcessingCommand(true);
    if (selectedTopic.isFolder) {
      await apiRequest({
        subRoute: "folder",
        method: "POST",
        path: "/delete_folder",
        body: { folderId: selectedTopic._id }
      });
    } else if (selectedTopic.sourceType === "Vojomo Document") {
      await apiRequest({
        subRoute: "topic",
        method: "POST",
        path: `/delete_topic`,
        body: {
          topicId: selectedTopic?._id
        }
      });
    } else {
      await apiRequest({
        subRoute: "ai",
        method: "POST",
        path: `/delete_source`,
        body: {
          sourceId: selectedTopic._id
        }
      });
    }
    setIsProcessingCommand(false);
    setIsDeleteModalVisible(false);
    getDocs(currentFolderId);
  };

  const handleBulkDelete = async () => {
    setIsProcessingCommand(true);

    // Separate folders and documents
    const selectedRecords = selectedRowKeys
      .map((key) => rowData?.find((row) => row.key === key))
      .filter((record) => record !== undefined);

    const folders = selectedRecords.filter((record) => record.isFolder);
    const documents = selectedRecords.filter((record) => !record.isFolder);

    // Notify user if folders were selected
    if (folders.length > 0) {
      message.warning(
        `Folders cannot be bulk deleted. Only ${documents.length} document${documents.length !== 1 ? "s" : ""} deleted.  Folders and their items will not be deleted.`
      );
    }

    // Only delete documents
    if (documents.length > 0) {
      await Promise.all(
        documents.map((record) => {
          if (record.sourceType === "Vojomo Document") {
            return apiRequest({
              subRoute: "topic",
              method: "POST",
              path: `/delete_topic`,
              body: {
                topicId: record._id
              }
            });
          } else {
            return apiRequest({
              subRoute: "ai",
              method: "POST",
              path: `/delete_source`,
              body: {
                sourceId: record._id
              }
            });
          }
        })
      );
    }

    setIsProcessingCommand(false);
    setIsDeleteModalVisible(false);
    setSelectedRowKeys([]);
    getDocs(currentFolderId);
  };

  const convertSourceToDataType = (source: any) => {
    const title = source.file_name;
    const isShared = source.owner !== vojomoAccount["_id"];

    let fmtType = "Upload";
    // Try to map mime types to friendlier names if possible
    if (source.file_type === "application/pdf") fmtType = "PDF";
    else if (source.file_type.includes("word")) fmtType = "Word Doc";
    else if (source.file_type.includes("text/plain")) fmtType = "Text File";
    else {
      // Fallback to extension
      const parts = source.file_name.split(".");
      if (parts.length > 1) {
        fmtType = parts[parts.length - 1].toUpperCase();
      }
    }

    return {
      title,
      displayName: title,
      updatedAt: new Date(source.updated_at),
      createdAt: source.created_at
        ? new Date(source.created_at)
        : new Date(source.updated_at),
      versions: "-",
      _id: source._id,
      key: `source-${source._id}`,
      type: source.file_type,
      fmtType: fmtType,
      isBuildingBlock: false,
      isShared,
      wip: false,
      isFolder: false,
      parentFolderId: null,
      memberName: "",
      ownerName: source.owner_name || "Unknown",
      owner: source.owner,
      isTeam: false,
      sourceType: fmtType
    } as DataType;
  };

  const convertTopicToDataType = (topic: Topic) => {
    const fmtType = (type: string) => {
      if (!type) {
        return "No type yet";
      }

      if (type === "document-writer") {
        return "Document";
      }
      if (type === "document-writer-subtopic") {
        return "Subtopic";
      }
      if (type === "briefing-doc") {
        return "Alignment Report";
      }

      if (type === "building-block") {
        return `Roadmap: ${topic["title"]}`;
      }

      let displayType =
        type.split("-").join(" ").charAt(0).toUpperCase() +
        type.split("-").join(" ").slice(1);

      return displayType.replace("Building block", "Roadmap Building Block");
    };

    const isBuildingBlock = ["building-block", "briefing-doc"].includes(
      topic["type"]
    );

    const title = (
      (topic["title"] == "" ? "Untitled Document" : topic["title"]) +
      (topic["wip"] ? " (WIP)" : "")
    ).replace("_", " ");

    return {
      title,
      displayName: topic["display_name"],
      updatedAt: new Date(topic["updated_at"]),
      createdAt: topic["created_at"]
        ? new Date(topic["created_at"])
        : new Date(topic["updated_at"]),
      versions: String(topic["outputs"]?.length || 0),
      _id: topic["_id"],
      key: `doc-${topic["_id"]}`,
      type: topic["type"],
      fmtType: fmtType(topic["type"]),
      isBuildingBlock: isBuildingBlock,
      isShared: !isBuildingBlock && topic["owner"] !== vojomoAccount["_id"],
      wip: topic["wip"],
      isFolder: false,
      parentFolderId: topic["parent_folder_id"],
      memberName: topic["member_name"],
      ownerName: topic["owner_name"],
      owner: topic["owner"],
      isTeam: topic["is_team"],
      sourceType: "Vojomo Document"
    } as DataType;
  };

  const getColumns = (excludeColumns: string[] = []) => {
    const defaultColumns: ColumnsType<DataType> = [
      {
        title: "Title",
        dataIndex: "title",
        width: "25%",
        key: "title",
        filterSearch: true,
        sorter: (a: any, b: any) => a.title.localeCompare(b.title),
        ...createAntdFilter<DataType>("title", "Title"),
        render: (_: any, record: DataType) => {
          if (record.isFolder) {
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  width: "100%"
                }}
                onClick={() => handleFolderClick(record._id)}
              >
                <FaFolder
                  style={{ color: "#2c1561", fontSize: "16px", flexShrink: 0 }}
                />
                <span style={{ flex: 1 }}>{record.title}</span>
                {record.isLocked && (
                  <Tooltip
                    title={record.isLocked ? "Folder can not be modified" : ""}
                  >
                    <FaLock
                      style={{
                        color: "#666",
                        fontSize: "12px",
                        flexShrink: 0,
                        marginLeft: "auto"
                      }}
                    />
                  </Tooltip>
                )}
              </div>
            );
          }

          const title =
            record.displayName ||
            (record.title == "" ? "Untitled Document" : record.title);

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FaFile style={{ color: "#666", flexShrink: 0 }} />
              {newTab ? (
                <a
                  href="#"
                  onClick={async (e) => {
                    e.preventDefault();
                    if (record.sourceType !== "Vojomo Document") {
                      handleFileClick(record._id, record.title, record.type);
                      return;
                    }

                    if (
                      record.isBuildingBlock &&
                      record.memberName !== vojomoAccount.impersonated_company
                    ) {
                      await updateImpersonatedCompany(record.memberName);
                    }

                    window.open(
                      record.isBuildingBlock
                        ? `${window.location.origin}/roadmap/build_roadmap?block=${record.title}&view=${record.isTeam ? "team" : "my"}`
                        : `${window.location.origin}/documents/docs/${record._id}`,
                      "_system"
                    );
                  }}
                >
                  {title || "Untitled"}
                </a>
              ) : (
                <Link
                  onClick={async (e) => {
                    if (record.sourceType !== "Vojomo Document") {
                      e.preventDefault();
                      handleFileClick(record._id, record.title, record.type);
                      return;
                    }
                    if (
                      record.isBuildingBlock &&
                      record.memberName !== vojomoAccount.impersonated_company
                    ) {
                      await updateImpersonatedCompany(record.memberName);
                    }
                  }}
                  to={
                    record.sourceType !== "Vojomo Document"
                      ? "#"
                      : record.isBuildingBlock
                        ? `/roadmap/build_roadmap?block=${record.title}&view=${record.isTeam ? "team" : "my"}`
                        : `/documents/docs/${record._id}`
                  }
                >
                  {title || "Untitled"}
                </Link>
              )}
            </div>
          );
        }
      },
      {
        title: "Date",
        dataIndex: "createdAt",
        key: "createdAt",
        width: "200px",
        ...createAntdFilter<DataType>("createdAt", "Date"),
        sorter: (a: DataType, b: DataType) =>
          a.createdAt.getTime() - b.createdAt.getTime(),
        render: (_: any, record: DataType) =>
          getFriendlyTimeAgo(record.createdAt)
      },

      {
        title: "Owner / Creator",
        dataIndex: "ownerName",
        key: "ownerName",
        ...createAntdFilter<DataType>("ownerName", "Owner / Creator"),
        render: (_: any, record: DataType) =>
          record.isFolder
            ? ""
            : `${record.ownerName} (${record.memberName || memberAccount.member_name})`
      },
      {
        title: "Shared Status",
        dataIndex: "isShared",
        key: "isShared",
        render: (_: any, record: DataType) => {
          if (record.isFolder) return "";
          return record.isShared ? "Shared" : "Private";
        }
      },
      {
        title: "Source Type",
        dataIndex: "sourceType",
        key: "sourceType",
        ...createAntdFilter<DataType>("sourceType", "Source Type"),
        render: (text: string, record: DataType) =>
          record.isFolder ? "" : text
      },
      {
        title: "Type",
        dataIndex: "fmtType",
        key: "fmtType",
        ...createAntdFilter<DataType>("fmtType", "Type"),
        render: (_: any, record: DataType) =>
          record.isFolder ? "Folder" : record.fmtType
      },
      {
        title: "",
        dataIndex: "action",
        width: 50,
        key: "action",
        render: (_: any, record: DataType) => {
          if (record.isLocked || record.isShared) {
            return null;
          }

          if (record.isBuildingBlock) {
            if (record.owner !== vojomoAccount["_id"]) {
              return null;
            }

            if (
              record.isTeam &&
              !memberAccount.owner?.includes(vojomoAccount["_id"])
            ) {
              return null;
            }
          }

          let menuItems = [];

          if (record.isFolder) {
            menuItems = [
              {
                key: "rename",
                label: "Rename",
                onClick: () => {
                  setSelectedTopic(record);
                  setIsRenameModalVisible(true);
                }
              },
              {
                key: "move",
                label: "Move to",
                onClick: async () => {
                  setSelectedTopic(record);
                  await getAllFolders();
                  setIsMoveModalVisible(true);
                }
              },

              {
                key: "delete",
                label: "Delete",
                onClick: () => {
                  setSelectedTopic(record);
                  setIsDeleteModalVisible(true);
                }
              }
            ];
          } else {
            menuItems = [
              {
                key: "delete",
                label: "Delete",
                onClick: () => {
                  setSelectedTopic(record);
                  setIsDeleteModalVisible(true);
                }
              }
            ];

            if (!record.isBuildingBlock) {
              const nonBuildingBlockItems = [
                {
                  key: "rename",
                  label: "Rename",
                  onClick: () => {
                    setSelectedTopic(record);
                    setIsRenameModalVisible(true);
                  }
                },
                {
                  key: "move",
                  label: "Move to",
                  onClick: async () => {
                    setSelectedTopic(record);
                    await getAllFolders();
                    setIsMoveModalVisible(true);
                  }
                },
                {
                  key: "share",
                  label: "Share",
                  onClick: async () => {
                    setSelectedTopic(record);
                    setIsShareModalVisible(true);
                  }
                }
              ];

              // Only allow Rename/Move for Vojomo Documents
              if (record.sourceType === "Vojomo Document") {
                menuItems = [...menuItems, ...nonBuildingBlockItems];
              } else {
                // For uploads, just Share (and Delete is already there)
                menuItems.push({
                  key: "share",
                  label: "Share",
                  onClick: async () => {
                    setSelectedTopic(record);
                    setIsShareModalVisible(true);
                  }
                });
              }
            }
          }

          return (
            <>
              <Dropdown trigger={["click"]} menu={{ items: menuItems }}>
                <Tooltip
                  title={record.isLocked ? "Folder can not be modified" : ""}
                >
                  <FaEllipsisVertical className="cursor-pointer" />
                </Tooltip>
              </Dropdown>
            </>
          );
        }
      }
    ];

    return defaultColumns.filter(
      (c) => !excludeColumns.includes(c.key as string)
    );
  };

  const rowSelection: TableProps<DataType>["rowSelection"] = {
    selectedRowKeys,
    onChange: (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
      trackEvent("selected_rows", {
        keys: selectedRowKeys,
        rows: selectedRows
      });

      setSelectedRowKeys(selectedRowKeys);
    },
    getCheckboxProps: (record: DataType) => ({
      disabled: record.isLocked, // Disable checkbox for locked folders
      name: record.title
    })
  };

  const handleFolderClick = async (folderId: string) => {
    setCurrentFolderId(folderId);
    // Update URL with folder parameter
    setSearchParams({ folder: folderId });

    await Promise.all([getDocs(folderId), getFolderPath(folderId)]);
  };

  const handleCreateFolder = async () => {
    setIsProcessingCommand(true);
    await apiRequest({
      subRoute: "folder",
      method: "POST",
      path: "/create_folder",
      body: {
        name: newFolderName || "New Folder",
        parent_folder_id: currentFolderId
      }
    });
    setIsProcessingCommand(false);
    setIsCreateFolderModalVisible(false);
    setNewFolderName("");
    getDocs(currentFolderId);
  };

  const getAllFolders = async () => {
    // Recursively fetch all folders for move destination picker
    const fetchAllFolders = async (
      parentId: string | null = null
    ): Promise<Folder[]> => {
      const folders = await apiRequest<Folder[]>({
        subRoute: "folder",
        path: `/get_folders${parentId ? `?parent_folder_id=${parentId}` : ""}`
      });

      let allFolders: Folder[] = [...folders];

      for (const folder of folders) {
        const subFolders = await fetchAllFolders(folder._id);
        allFolders = [...allFolders, ...subFolders];
      }

      return allFolders;
    };

    const folders = await fetchAllFolders();
    setAllFolders(folders);
  };

  const handleMove = async () => {
    if (!selectedTopic) return;

    setIsProcessingCommand(true);
    try {
      if (selectedTopic.isFolder) {
        await apiRequest({
          subRoute: "folder",
          method: "POST",
          path: "/move_folder",
          body: {
            folderId: selectedTopic._id,
            newParentId: selectedDestinationFolder
          }
        });
      } else {
        await apiRequest({
          subRoute: "folder",
          method: "POST",
          path: "/move_document_to_folder",
          body: {
            topicId: selectedTopic._id,
            folderId: selectedDestinationFolder
          }
        });
      }

      setIsMoveModalVisible(false);
      setSelectedDestinationFolder(null);
      await getDocs(currentFolderId);
    } catch (error) {
      trackError("error_moving_item", error);
    } finally {
      setIsProcessingCommand(false);
    }
  };

  const handleBulkMove = async () => {
    setIsProcessingCommand(true);

    // Separate folders and documents
    const selectedRecords = selectedRowKeys
      .map((key) => rowData?.find((row) => row.key === key))
      .filter((record) => record !== undefined);

    const folders = selectedRecords.filter((record) => record.isFolder);
    const documents = selectedRecords.filter((record) => !record.isFolder);

    // Notify user if folders were selected
    if (folders.length > 0) {
      message.warning(
        `Folders cannot be bulk moved. Only ${documents.length} document${documents.length !== 1 ? "s" : ""} will be moved.`
      );
    }

    // Only move documents
    if (documents.length > 0) {
      await Promise.all(
        documents.map((record) => {
          return apiRequest({
            subRoute: "folder",
            method: "POST",
            path: "/move_document_to_folder",
            body: {
              topicId: record._id,
              folderId: selectedDestinationFolder
            }
          });
        })
      );
    }

    setIsProcessingCommand(false);
    setIsBulkMoveModalVisible(false);
    setSelectedDestinationFolder(null);
    setSelectedRowKeys([]);
    getDocs(currentFolderId);
  };

  const getFolderPath = async (folderId: string | null) => {
    if (!folderId) {
      setFolderPath([]);
      return;
    }

    const path = await apiRequest<{ _id: string; name: string }[]>({
      subRoute: "folder",
      path: `/get_folder_path?folder_id=${folderId}`
    });
    setFolderPath(path);
  };

  const filteredData = useMemo(() => {
    if (!rowData || !searchTerm) return rowData;

    const lowerSearch = searchTerm.toLowerCase();
    return rowData.filter((row) => {
      const title = (row.title || "").toLowerCase();
      const type = (row.type || "").toLowerCase();
      return title.includes(lowerSearch) || type.includes(lowerSearch);
    });
  }, [rowData, searchTerm]);

  const getDocs = async (folderId: string | null = null) => {
    // Reset table ready state when fetching new data
    setIsLoadingDocs(true);

    // Check if current folder is locked
    if (folderId) {
      const currentFolder = await apiRequest<Folder>({
        subRoute: "folder",
        path: `/get_folder?folder_id=${folderId}`
      });

      setIsCurrentFolderLocked(currentFolder?.is_locked || false);
    } else {
      setIsCurrentFolderLocked(false);
    }

    // Fetch folders

    const getFoldersPromise = apiRequest<Folder[]>({
      subRoute: "folder",
      path: `/get_folders${folderId ? `?parent_folder_id=${folderId}` : ""}`
    });

    let path = "/get_topics?";

    if (filteredId) {
      path += `filtered_id=${filteredId}&`;
    }

    if (folderId) {
      path += `parent_folder_id=${folderId}`;
    }

    const getTopicsPromise = apiRequest<(Topic & { member_name: string })[]>({
      subRoute: "topic",
      path
    });

    const promisesArray: Promise<any>[] = [getFoldersPromise, getTopicsPromise];

    // Fetch sources (only if at root level)
    if (!folderId) {
      const getSourcesPromise = apiRequest<any[]>({
        subRoute: "ai",
        path: filteredId
          ? `/retrieve_sources?filtered_id=${filteredId}`
          : `/retrieve_sources`,
        method: "GET"
      }).then((sourcesData) => {
        // Filter out sources that are actually topics (file_type starts with "topic/")
        const realSources = sourcesData.filter(
          (s) => !s.file_type.startsWith("topic/")
        );

        return realSources.map(convertSourceToDataType);
      });

      promisesArray.push(getSourcesPromise);
    }

    const results = await Promise.all(promisesArray);

    const folders = results[0];
    const topics = results[1];

    let sourceRows: DataType[] = [];
    if (results.length === 3) {
      sourceRows = results[2];
    }

    // Filter documents by current folder
    const filteredTopics = topics.filter((topic: Topic) => {
      const topicFolderId = topic.parent_folder_id;
      if (folderId) {
        return topicFolderId === folderId;
      } else {
        return !topicFolderId;
      }
    });

    // Map documents to row data
    const documentRows = filteredTopics.map(convertTopicToDataType);

    // Map folders to row data
    const folderRows = folders.map((folder: Folder) => {
      return {
        title: folder.name,
        updatedAt: new Date(folder.updated_at),
        createdAt: new Date(folder.created_at || folder.updated_at), // fallback to updated_at if created_at is null
        versions: "-",
        _id: folder._id,
        key: `folder-${folder._id}`,
        type: "Folder",
        isFolder: true,
        parentFolderId: folder.parent_folder_id,
        isLocked: folder.is_locked,
        memberName: "",
        sourceType: "Vojomo Document" as const // Folders are technically part of the system
      };
    });

    // Combine folders and documents
    // Documents include topics and sources
    const allRows = [...folderRows, ...documentRows, ...sourceRows];

    // Sort by updatedAt (newest first), keeping folders at the top
    const sortedRows = allRows.sort((a, b) => {
      // Keep folders at the top
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;

      // Sort by updatedAt descending (newest first)
      return b.updatedAt.getTime() - a.updatedAt.getTime();
    });

    setRowData(sortedRows);

    // Wait for the table to paint before showing breadcrumbs/toolbar
    setIsLoadingDocs(false);
  };

  // Sync with URL params - responds to browser back/forward
  useEffect(() => {
    const folderIdFromUrl = searchParams.get("folder");
    if (folderIdFromUrl) {
      setCurrentFolderId(folderIdFromUrl);
      getDocs(folderIdFromUrl);
      getFolderPath(folderIdFromUrl);
    } else {
      setCurrentFolderId(null);
      setFolderPath([]);
      getDocs(null);
    }
  }, [searchParams]);

  // Mobile card renderer
  const renderMobileCard = (record: DataType) => {
    const isLocked = record.isLocked || record.isShared;
    const canShowActions =
      !isLocked &&
      (!record.isBuildingBlock ||
        (record.owner === vojomoAccount["_id"] &&
          (!record.isTeam ||
            memberAccount.owner?.includes(vojomoAccount["_id"]))));

    return (
      <div
        key={record.key}
        className="bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm hover:shadow-md transition-shadow"
        style={{ minHeight: "80px" }}
      >
        <div className="flex items-start justify-between gap-3">
          {/* Icon and Content */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Icon */}
            <div className="mt-1 flex-shrink-0">
              {record.isFolder ? (
                <FaFolder style={{ color: "#2c1561", fontSize: "20px" }} />
              ) : (
                <FaFile style={{ color: "#666", fontSize: "18px" }} />
              )}
            </div>

            {/* Content */}
            <div
              className="flex-1 min-w-0"
              onClick={async () => {
                if (record.isFolder) {
                  handleFolderClick(record._id);
                } else if (record.sourceType !== "Vojomo Document") {
                  handleFileClick(record._id, record.title, record.type);
                } else {
                  if (
                    record.isBuildingBlock &&
                    record.memberName !== vojomoAccount.impersonated_company
                  ) {
                    await updateImpersonatedCompany(record.memberName);
                  }
                  navigate(
                    record.isBuildingBlock
                      ? `/roadmap/build_roadmap?block=${record.title}&view=${record.isTeam ? "team" : "my"}`
                      : `/documents/docs/${record._id}`
                  );
                }
              }}
            >
              {/* Title */}
              <div className="font-semibold text-[#2c1561] text-base truncate flex items-center gap-2">
                <span className="truncate">
                  {record.displayName || record.title || "Untitled"}
                </span>
                {record.isLocked && (
                  <FaLock
                    style={{ color: "#666", fontSize: "12px", flexShrink: 0 }}
                  />
                )}
              </div>

              {/* Metadata */}
              <div className="text-sm text-gray-600 mt-1 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {!record.isFolder && (
                    <>
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                        {record.fmtType}
                      </span>
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                        {record.isShared ? "Shared" : "Private"}
                      </span>
                    </>
                  )}
                  {record.isFolder && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                      Folder
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500">
                  {getFriendlyTimeAgo(record.createdAt)}
                </div>
              </div>
            </div>
          </div>

          {/* Action Button */}
          {canShowActions && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setMobileActionItem(record);
                setMobileActionSheet(true);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-w-touch min-h-touch flex items-center justify-center flex-shrink-0"
              aria-label="More actions"
            >
              <FaEllipsisVertical className="text-gray-600" size={18} />
            </button>
          )}
        </div>
      </div>
    );
  };

  if (isLoadingDocs) {
    return <VojomoLoader />;
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultBorderColor: "rgb(44, 21, 97)",
            defaultHoverBorderColor: "rgb(64, 41, 117)", // 20 pxs lighter
            defaultBg: "rgb(44, 21, 97)",
            defaultHoverBg: "rgb(64, 41, 117)", // 20 pxs lighter
            defaultColor: "white"
          }
        }
      }}
    >
      {!rowData ? (
        <VojomoLoader />
      ) : (
        <div>
          <div className="flex items-center gap-3">
            <h3 className="m-0">Studio</h3>
            <MemberMarker />
          </div>

          {/* Artifact type filter */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {[
              { label: "Text Documents", active: true },
              { label: "Audio", active: false },
              { label: "Video", active: false },
              { label: "Slide Decks", active: false },
              { label: "Images", active: false },
              { label: "Visual / Graphic", active: false }
            ].map((type) => (
              <Tooltip
                key={type.label}
                title={!type.active ? "Coming Soon" : undefined}
              >
                <span
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    type.active
                      ? "bg-[#2c1561] text-white border-[#2c1561] cursor-pointer"
                      : "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
                  }`}
                >
                  {type.label}
                  {!type.active && (
                    <span className="ml-1.5 text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full">
                      Soon
                    </span>
                  )}
                </span>
              </Tooltip>
            ))}
          </div>
          {/* Team Alignment Reports from Roadmap Builder sessions */}
          <TeamAlignmentReportsSection />

          {/* Create Note Modal */}
          <CreateNoteModal
            isOpen={createNoteHook.isModalOpen}
            noteName={createNoteHook.noteName}
            noteContent={createNoteHook.noteContent}
            isCreating={createNoteHook.isCreating}
            error={createNoteHook.error}
            isNameTaken={createNoteHook.isNameTaken}
            onClose={createNoteHook.closeModal}
            onNameChange={createNoteHook.handleNameChange}
            onContentChange={createNoteHook.setNoteContent}
            onCreate={createNoteHook.createNote}
          />

          {/* File Upload Input */}
          <input
            id="fileInput"
            accept=".pdf,.doc,.docx,.txt"
            type="file"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {/* Modals */}
          {sameNameModalOpen && (
            <Modal
              open={true}
              onCancel={() => {
                setSameNameModalOpen(false);
                setNewFileName(null);
                setIsNewFileNameTaken(false);
                setErrorMessage(null);
                setIgnoreSameHash(false);
              }}
              footer={[
                <Button
                  key="back"
                  onClick={() => {
                    setSameNameModalOpen(false);
                    setNewFileName(null);
                    setIsNewFileNameTaken(false);
                    setErrorMessage(null);
                    setIgnoreSameHash(false);
                  }}
                  style={{
                    color: "black",
                    borderColor: "#d9d9d9",
                    backgroundColor: "white"
                  }}
                >
                  Cancel
                </Button>,
                <Tooltip
                  title={
                    !newFileName || newFileName.trim() === ""
                      ? "Please enter a new file name"
                      : newFileName === file?.name
                        ? "Please choose a different name than the original"
                        : isNewFileNameTaken
                          ? "This filename is already taken"
                          : ""
                  }
                >
                  <Button
                    key="submit"
                    loading={isLoading}
                    disabled={
                      !newFileName ||
                      newFileName.trim() === "" ||
                      newFileName === file?.name ||
                      isNewFileNameTaken
                    }
                    onClick={handleRenameAndUpload}
                    style={{
                      color:
                        !newFileName ||
                        newFileName.trim() === "" ||
                        newFileName === file?.name ||
                        isNewFileNameTaken
                          ? "black"
                          : "white"
                    }}
                  >
                    Upload with new name
                  </Button>
                </Tooltip>
              ]}
            >
              <p className="font-bold">
                File name already exists! Please rename your file.
              </p>
              <p>
                Enter a new name (extension will be preserved automatically):
              </p>
              <Input
                placeholder="New file name without extension"
                value={
                  newFileName
                    ? newFileName.includes(".")
                      ? newFileName.substring(0, newFileName.lastIndexOf("."))
                      : newFileName
                    : file?.name
                      ? file.name.substring(0, file.name.lastIndexOf("."))
                      : ""
                }
                onChange={async (e) => {
                  const value = e.target.value;
                  setNewFileName(value);

                  if (isNewFileNameTaken) {
                    setIsNewFileNameTaken(false);
                    setErrorMessage(null);
                  }

                  const valueWithExt = file
                    ? ensureFileExtension(value, file.name)
                    : value;

                  if (
                    value &&
                    value.trim() !== "" &&
                    valueWithExt !== file?.name
                  ) {
                    if (checkFileNameTimeoutRef.current) {
                      clearTimeout(checkFileNameTimeoutRef.current);
                    }

                    checkFileNameTimeoutRef.current = setTimeout(async () => {
                      const valueWithExt = file
                        ? ensureFileExtension(value, file.name)
                        : value;
                      const exists = await checkFileNameExists(valueWithExt);
                      setIsNewFileNameTaken(exists);
                      if (exists) {
                        setErrorMessage(
                          `The name "${valueWithExt}" is already taken. Please choose another name.`
                        );
                      }
                    }, 500);
                  }
                }}
              />
              {isNewFileNameTaken && (
                <div style={{ color: "red", marginTop: "8px" }}>
                  This filename is already taken. Please choose another name.
                </div>
              )}
            </Modal>
          )}

          {/* File Preview Modal */}
          {isFilePreviewModalVisible && (
            <Modal
              open={true}
              width="80%"
              style={{ top: 20, maxHeight: "90vh" }}
              bodyProps={{
                style: {
                  padding: "20px",
                  height: "calc(90vh - 110px)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column"
                }
              }}
              onCancel={() => {
                setIsFilePreviewModalVisible(false);
                setSelectedFileUrl(null);
                setSelectedFileName(null);
                setSelectedFileType(null);
              }}
              footer={[
                <Button
                  key="close"
                  onClick={() => {
                    setIsFilePreviewModalVisible(false);
                    setSelectedFileUrl(null);
                    setSelectedFileName(null);
                    setSelectedFileType(null);
                  }}
                >
                  Close
                </Button>
              ]}
            >
              <div style={{ marginBottom: "16px", flexShrink: 0 }}>
                <h3 style={{ margin: 0 }}>{selectedFileName}</h3>
              </div>
              {isLoadingFileUrl ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <VojomoLoader />
                </div>
              ) : selectedFileUrl ? (
                <div
                  style={{
                    width: "100%",
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column"
                  }}
                >
                  {(() => {
                    const fileType = selectedFileType?.toLowerCase() || "";
                    const fileName = selectedFileName?.toLowerCase() || "";
                    const isPdf =
                      fileType === "application/pdf" || fileType === "pdf";
                    const isDocx =
                      fileType.includes("word") ||
                      fileType === "docx" ||
                      fileName.endsWith(".docx");
                    const isImage =
                      fileType.includes("image") ||
                      [
                        "jpg",
                        "jpeg",
                        "png",
                        "gif",
                        "bmp",
                        "webp",
                        "svg"
                      ].includes(fileType);
                    const isText =
                      fileType === "text/plain" ||
                      [
                        "txt",
                        "md",
                        "json",
                        "xml",
                        "csv",
                        "log",
                        "yaml",
                        "yml",
                        "html",
                        "css",
                        "js",
                        "ts",
                        "tsx",
                        "jsx",
                        "py",
                        "java",
                        "c",
                        "cpp",
                        "h",
                        "sh"
                      ].includes(fileType);

                    if (isDocx || isPdf) {
                      // Use Google Docs Viewer for DOCX, direct URL for PDF
                      const viewerUrl = isDocx
                        ? `https://docs.google.com/viewer?url=${encodeURIComponent(selectedFileUrl)}&embedded=true`
                        : selectedFileUrl;

                      return (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column"
                          }}
                        >
                          <iframe
                            src={viewerUrl}
                            style={{
                              width: "100%",
                              flex: 1,
                              border: "none",
                              minHeight: 0
                            }}
                            title={selectedFileName || "File Preview"}
                            onError={() => {
                              trackError("file_iframe_failed_to_load");
                            }}
                          />
                          <div
                            style={{
                              textAlign: "center",
                              padding: "12px 0",
                              borderTop: "1px solid #f0f0f0",
                              flexShrink: 0
                            }}
                          >
                            <a
                              href={selectedFileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button type="link" size="small">
                                Download File
                              </Button>
                            </a>
                          </div>
                        </div>
                      );
                    } else if (isImage) {
                      return (
                        <img
                          src={selectedFileUrl}
                          alt={selectedFileName || "File Preview"}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain"
                          }}
                        />
                      );
                    } else if (isText) {
                      return (
                        <iframe
                          src={selectedFileUrl}
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none"
                          }}
                          title={selectedFileName || "File Preview"}
                        />
                      );
                    } else {
                      return (
                        <div style={{ textAlign: "center", padding: "40px" }}>
                          <p>Preview not available for this file type.</p>
                          <a
                            href={selectedFileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button type="primary">Download File</Button>
                          </a>
                        </div>
                      );
                    }
                  })()}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <p>Failed to load file preview.</p>
                </div>
              )}
            </Modal>
          )}

          {sameHashModalOpen && (
            <Modal
              open={true}
              onCancel={() => {
                setSameHashModalOpen(false);
                setIgnoreSameHash(false);
              }}
              footer={[
                <Button
                  key="back"
                  onClick={() => {
                    setSameHashModalOpen(false);
                    setIgnoreSameHash(false);
                  }}
                  style={{
                    color: "black",
                    borderColor: "#d9d9d9",
                    backgroundColor: "white"
                  }}
                >
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  loading={isLoading}
                  onClick={() => {
                    setIgnoreSameHash(true);
                    setSameHashModalOpen(false);
                    if (file) {
                      processFileUpload(file, true);
                    }
                  }}
                  style={{ color: "white" }}
                >
                  Continue
                </Button>
              ]}
            >
              <p className="font-bold">
                File content matches the below file(s)!
              </p>
              {sameHashModalFiles.map((file, i) => (
                <p key={i}>{file.file_name}</p>
              ))}
              <p className="font-bold mt-10">
                Are you sure you want to continue uploading this file?
              </p>
            </Modal>
          )}

          {isDeleteModalVisible && (
            <DeleteModal
              loading={isProcessingCommand}
              topicName={
                selectedRowKeys.length > 0
                  ? `${selectedRowKeys.length} item${selectedRowKeys.length > 1 ? "s" : ""}`
                  : selectedTopic?.title
              }
              topicType={selectedTopic?.type}
              confirmationText={
                selectedRowKeys.length > 0
                  ? `Are you sure you want to delete ${selectedRowKeys.length} item${selectedRowKeys.length > 1 ? "s" : ""} and all of their versions?`
                  : selectedTopic?.isFolder
                    ? `Are you sure you want to delete the folder "${selectedTopic?.title}"? Documents inside will be moved to the root level.`
                    : `Are you sure you want to delete ${selectedTopic?.title} and all of its versions?`
              }
              onCancel={() => setIsDeleteModalVisible(false)}
              onSubmit={() => {
                if (selectedRowKeys.length > 0) {
                  handleBulkDelete();
                } else {
                  handleDelete();
                }
              }}
            />
          )}
          {isRenameModalVisible && (
            <Modal
              className="w-[400px]"
              open={true}
              closable={true}
              onCancel={() => setIsRenameModalVisible(false)}
              footer={[
                <Button
                  key="back"
                  onClick={() => {
                    setNewTopicName("");
                    setIsRenameModalVisible(false);
                  }}
                >
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  loading={isProcessingCommand}
                  onClick={async () => {
                    if (selectedTopic?.isFolder) {
                      await apiRequest({
                        subRoute: "folder",
                        method: "POST",
                        path: "/update_folder",
                        body: {
                          folderId: selectedTopic._id,
                          name: newTopicName
                        }
                      });
                    } else {
                      await apiRequest({
                        subRoute: "topic",
                        method: "POST",
                        path: "/update_title",
                        body: {
                          topicId: selectedTopic._id,
                          title: newTopicName
                        }
                      });
                    }

                    getDocs(currentFolderId);
                    setIsRenameModalVisible(false);
                  }}
                >
                  Rename
                </Button>
              ]}
            >
              <p className="font-bold">Rename</p>
              <Input
                placeholder="New name"
                value={newTopicName || selectedTopic?.title}
                onChange={(e) => setNewTopicName(e.target.value)}
              />
            </Modal>
          )}

          <Modal
            className="w-[400px]"
            open={isCreateFolderModalVisible}
            closable={true}
            onCancel={() => setIsCreateFolderModalVisible(false)}
            footer={[
              <Button
                key="back"
                onClick={() => {
                  setNewFolderName("");
                  setIsCreateFolderModalVisible(false);
                }}
              >
                Cancel
              </Button>,
              <Button
                key="submit"
                loading={isProcessingCommand}
                onClick={handleCreateFolder}
              >
                Create Folder
              </Button>
            ]}
          >
            <p className="font-bold">Create New Folder</p>
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
            />
          </Modal>

          {/* Move Modal */}
          {isMoveModalVisible && (
            <Modal
              className="w-[500px]"
              open={true}
              closable={true}
              onCancel={() => {
                setIsMoveModalVisible(false);
                setSelectedDestinationFolder(null);
              }}
              footer={[
                <Button
                  key="back"
                  onClick={() => {
                    setIsMoveModalVisible(false);
                    setSelectedDestinationFolder(null);
                  }}
                >
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  loading={isProcessingCommand}
                  onClick={handleMove}
                >
                  Move
                </Button>
              ]}
            >
              <p className="mb-4">
                Move <b>{selectedTopic?.title}</b> to:
              </p>
              <Select
                style={{ width: "100%" }}
                placeholder="Select destination folder"
                value={selectedDestinationFolder}
                onChange={(value) => setSelectedDestinationFolder(value)}
                optionLabelProp="cleanLabel"
                options={[
                  {
                    label: (
                      <span style={{ paddingLeft: "0px" }}>
                        <FaFolder
                          className="inline mr-2"
                          style={{ color: "rgb(44, 21, 97)" }}
                        />{" "}
                        Root (No folder)
                      </span>
                    ),
                    cleanLabel: (
                      <span>
                        <FaFolder
                          className="inline mr-2"
                          style={{ color: "rgb(44, 21, 97)" }}
                        />{" "}
                        Root (No folder)
                      </span>
                    ),
                    value: null
                  },
                  ...allFolders
                    .filter((folder) => {
                      // Don't show the item being moved in the list
                      if (
                        (selectedTopic?.isFolder &&
                          folder._id === selectedTopic._id) ||
                        folder.is_locked
                      ) {
                        return false;
                      }
                      // Don't show descendants of the folder being moved
                      if (selectedTopic?.isFolder) {
                        // Check if this folder is a descendant
                        let currentId = folder.parent_folder_id;
                        while (currentId) {
                          if (currentId === selectedTopic._id) {
                            return false;
                          }
                          const parent = allFolders.find(
                            (f) => f._id === currentId
                          );
                          currentId = parent.parent_folder_id;
                        }
                      }
                      return true;
                    })
                    .map((folder) => {
                      // Calculate folder depth for indentation
                      let depth = 0;
                      let currentId = folder.parent_folder_id;
                      while (currentId) {
                        depth++;
                        const parent = allFolders.find(
                          (f) => f._id === currentId
                        );
                        currentId = parent.parent_folder_id;
                      }
                      return {
                        label: (
                          <span style={{ paddingLeft: `${depth * 20}px` }}>
                            <FaFolder
                              className="inline mr-2"
                              style={{ color: "rgb(44, 21, 97)" }}
                            />{" "}
                            {folder.name}
                          </span>
                        ),
                        cleanLabel: (
                          <span>
                            <FaFolder
                              className="inline mr-2"
                              style={{ color: "rgb(44, 21, 97)" }}
                            />{" "}
                            {folder.name}
                          </span>
                        ),
                        value: folder._id
                      };
                    })
                ]}
              />
            </Modal>
          )}

          {isShareModalVisible && (
            <ShareAccessModal
              refId={selectedTopic._id}
              refType={
                selectedTopic.sourceType === "Vojomo Document"
                  ? "topic"
                  : "source"
              }
              onClose={() => setIsShareModalVisible(false)}
            />
          )}

          {/* Bulk Move Modal */}
          {isBulkMoveModalVisible && (
            <Modal
              className="w-[500px]"
              open={true}
              closable={true}
              onCancel={() => {
                setIsBulkMoveModalVisible(false);
                setSelectedDestinationFolder(null);
              }}
              footer={[
                <Button
                  key="back"
                  onClick={() => {
                    setIsBulkMoveModalVisible(false);
                    setSelectedDestinationFolder(null);
                  }}
                >
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  loading={isProcessingCommand}
                  onClick={handleBulkMove}
                >
                  Move
                </Button>
              ]}
            >
              <p className="mb-4">
                Move <b>{selectedRowKeys.length}</b> selected item
                {selectedRowKeys.length !== 1 ? "s" : ""} to:
              </p>
              <Select
                style={{ width: "100%" }}
                placeholder="Select destination folder"
                value={selectedDestinationFolder}
                onChange={(value) => setSelectedDestinationFolder(value)}
                optionLabelProp="cleanLabel"
                options={[
                  {
                    label: (
                      <span style={{ paddingLeft: "0px" }}>
                        <FaFolder
                          className="inline mr-2"
                          style={{ color: "rgb(44, 21, 97)" }}
                        />{" "}
                        Root (No folder)
                      </span>
                    ),
                    cleanLabel: (
                      <span>
                        <FaFolder
                          className="inline mr-2"
                          style={{ color: "rgb(44, 21, 97)" }}
                        />{" "}
                        Root (No folder)
                      </span>
                    ),
                    value: null
                  },
                  ...allFolders
                    .filter((f) => !f.is_locked)
                    .map((folder) => {
                      // Calculate folder depth for indentation
                      let depth = 0;
                      let currentId = folder.parent_folder_id;
                      while (currentId) {
                        depth++;
                        const parent = allFolders.find(
                          (f) => f._id === currentId
                        );
                        currentId = parent.parent_folder_id;
                      }
                      return {
                        label: (
                          <span style={{ paddingLeft: `${depth * 20}px` }}>
                            <FaFolder
                              className="inline mr-2"
                              style={{ color: "rgb(44, 21, 97)" }}
                            />{" "}
                            {folder.name}
                          </span>
                        ),
                        cleanLabel: (
                          <span>
                            <FaFolder
                              className="inline mr-2"
                              style={{ color: "rgb(44, 21, 97)" }}
                            />{" "}
                            {folder.name}
                          </span>
                        ),
                        value: folder._id
                      };
                    })
                ]}
              />
            </Modal>
          )}

          {/* Breadcrumb Navigation */}
          <div
            style={{
              marginBottom: "16px",
              overflowX: isMobile ? "auto" : "visible",
              whiteSpace: "nowrap",
              WebkitOverflowScrolling: "touch"
            }}
            className={isMobile ? "scrollbar-hide" : ""}
          >
            <Breadcrumb
              style={{
                display: "inline-block"
              }}
            >
              <Breadcrumb.Item>
                <a
                  onClick={() => {
                    setCurrentFolderId(null);
                    // Clear folder parameter from URL
                    setSearchParams({});
                    getDocs(null);
                    setFolderPath([]);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  Studio
                </a>
              </Breadcrumb.Item>
              {folderPath.map((folder, index) => (
                <Breadcrumb.Item key={folder._id}>
                  {index === folderPath.length - 1 ? (
                    <span>{folder.name}</span>
                  ) : (
                    <a
                      onClick={() => {
                        setCurrentFolderId(folder._id);
                        // Update URL with folder parameter
                        setSearchParams({ folder: folder._id });
                        getDocs(folder._id);
                        getFolderPath(folder._id);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      {folder.name}
                    </a>
                  )}
                </Breadcrumb.Item>
              ))}
            </Breadcrumb>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              gap: isMobile ? "12px" : "16px",
              marginBottom: "16px",
              height: isMobile ? "auto" : "50px"
            }}
          >
            {!isMobile && (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "document",
                      label: "New Document",
                      icon: <FaFile />,
                      onClick: async () => {
                        trackEvent("created_new_topic", { currentFolderId });

                        const topicId = await apiRequest({
                          subRoute: "ai",
                          path: `/setup_new_topic${currentFolderId ? `?parent_folder_id=${currentFolderId}` : ""}`
                        });

                        newTab
                          ? window.open(`/documents/docs/${topicId}`, "_system")
                          : navigate(`/documents/docs/${topicId}`);
                      }
                    },
                    {
                      key: "folder",
                      label: "New Folder",
                      icon: <FaFolder />,
                      onClick: () => setIsCreateFolderModalVisible(true)
                    },
                    {
                      key: "upload",
                      label: "Upload Document",
                      icon: <FaPlus />,
                      onClick: () => {
                        document.getElementById("fileInput").click();
                      }
                    },
                    {
                      key: "note",
                      label: "Create Note",
                      icon: <FaFile />,
                      onClick: createNoteHook.openModal
                    }
                  ]
                }}
                trigger={["click"]}
              >
                <Tooltip
                  title={
                    isCurrentFolderLocked
                      ? "Cannot create or move documents in a locked folder"
                      : ""
                  }
                >
                  <Button
                    style={{ color: "white", width: "200px" }}
                    type="default"
                    disabled={isCurrentFolderLocked}
                    loading={isLoading}
                  >
                    {isLoading ? (
                      "Uploading..."
                    ) : (
                      <>
                        <FaPlus className="inline mr-2" /> Create New{" "}
                        <FaChevronDown className="inline ml-2" />
                      </>
                    )}
                  </Button>
                </Tooltip>
              </Dropdown>
            )}

            {selectedRowKeys.length > 0 && !isMobile && (
              <>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: "move",
                        disabled: isCurrentFolderLocked,
                        label: `Move ${selectedRowKeys.length} item${selectedRowKeys.length > 1 ? "s" : ""}`,
                        onClick: async () => {
                          await getAllFolders();
                          setIsBulkMoveModalVisible(true);
                        }
                      },
                      {
                        key: "delete",
                        label: `Delete ${selectedRowKeys.length} item${selectedRowKeys.length > 1 ? "s" : ""}`,
                        danger: true,
                        onClick: () => {
                          setIsDeleteModalVisible(true);
                        }
                      }
                    ]
                  }}
                  trigger={["click"]}
                >
                  <Button style={{ width: "200px" }}>
                    {selectedRowKeys.length} item
                    {selectedRowKeys.length > 1 ? "s" : ""} selected{" "}
                    <FaChevronDown className="inline ml-2" />
                  </Button>
                </Dropdown>
              </>
            )}

            <Input.Search
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={(value) => setSearchTerm(value)}
              allowClear
              style={{ width: isMobile ? "100%" : 300 }}
              className="custom-search-input"
            />
            <style>{`
            .custom-search-input .ant-input-search-button {
              background-color: white !important;
              border-color: #d9d9d9 !important;
            }
            .custom-search-input .ant-input-search-button:hover {
              background-color: white !important;
              border-color: #d9d9d9 !important;
            }
            .custom-search-input .ant-btn-icon {
              color: #000000 !important;
            }
          `}</style>
          </div>

          {/* Alerts */}
          <div style={{ width: "500px", marginBottom: "16px" }}>
            {errorMessage && (
              <Alert
                message={errorMessage}
                type="error"
                closable
                afterClose={() => setErrorMessage(null)}
                className="auto-close-alert"
              />
            )}
            {gcsPath && (
              <Alert
                message="File uploaded successfully!"
                type="success"
                closable
                afterClose={() => {
                  setGcsPath(null);
                  getDocs(currentFolderId);
                }}
                className="auto-close-alert"
              />
            )}
          </div>

          <div
            id="memberDataGrid"
            style={{
              ...style
            }}
          >
            {isMobile ? (
              /* Mobile Card View */
              <div className="space-y-2">
                {filteredData && filteredData.length > 0 ? (
                  filteredData.map(renderMobileCard)
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No documents found
                  </div>
                )}
              </div>
            ) : (
              /* Desktop Table View */
              <Table
                rowSelection={
                  isCurrentFolderLocked || filteredData.length === 0
                    ? null
                    : { type: "checkbox", ...rowSelection }
                }
                columns={getColumns(isCurrentFolderLocked ? ["isShared"] : [])}
                dataSource={filteredData}
                scroll={{ y: tableHeight }}
                pagination={false}
                className="mt-10"
                locale={{ emptyText: "No documents found" }}
              />
            )}
          </div>

          {/* Mobile FAB for Quick Create */}
          {isMobile && !isCurrentFolderLocked && (
            <TouchFAB
              icon={<FaPlus size={24} color="white" />}
              onClick={() => setMobileCreateSheet(true)}
              position="bottom-right"
            />
          )}

          {/* Mobile Create Bottom Sheet */}
          {isMobile && (
            <BottomSheet
              isOpen={mobileCreateSheet}
              onClose={() => setMobileCreateSheet(false)}
              title="Create New"
            >
              <div className="space-y-2 p-4">
                <button
                  onClick={async () => {
                    setMobileCreateSheet(false);
                    trackEvent("created_new_topic", { currentFolderId });
                    const topicId = await apiRequest({
                      subRoute: "ai",
                      path: `/setup_new_topic${currentFolderId ? `?parent_folder_id=${currentFolderId}` : ""}`
                    });
                    navigate(`/documents/docs/${topicId}`);
                  }}
                  className="w-full flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-touch"
                >
                  <FaFile className="text-[#2c1561]" size={20} />
                  <span className="font-medium text-[#2c1561]">
                    New Document
                  </span>
                </button>

                <button
                  onClick={() => {
                    setMobileCreateSheet(false);
                    setIsCreateFolderModalVisible(true);
                  }}
                  className="w-full flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-touch"
                >
                  <FaFolder className="text-[#2c1561]" size={20} />
                  <span className="font-medium text-[#2c1561]">New Folder</span>
                </button>

                <button
                  onClick={() => {
                    setMobileCreateSheet(false);
                    document.getElementById("fileInput").click();
                  }}
                  className="w-full flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-touch"
                >
                  <FaPlus className="text-[#2c1561]" size={20} />
                  <span className="font-medium text-[#2c1561]">
                    Upload Document
                  </span>
                </button>

                <button
                  onClick={() => {
                    setMobileCreateSheet(false);
                    createNoteHook.openModal();
                  }}
                  className="w-full flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-touch"
                >
                  <FaFile className="text-[#2c1561]" size={20} />
                  <span className="font-medium text-[#2c1561]">
                    Create Note
                  </span>
                </button>
              </div>
            </BottomSheet>
          )}

          {/* Mobile Action Bottom Sheet */}
          {isMobile && mobileActionItem && (
            <BottomSheet
              isOpen={mobileActionSheet}
              onClose={() => {
                setMobileActionSheet(false);
                setMobileActionItem(null);
              }}
              title={mobileActionItem.title}
            >
              <div className="space-y-2 p-4">
                {/* Rename */}
                {(mobileActionItem.isFolder ||
                  (!mobileActionItem.isBuildingBlock &&
                    mobileActionItem.sourceType === "Vojomo Document")) && (
                  <button
                    onClick={() => {
                      setSelectedTopic(mobileActionItem);
                      setIsRenameModalVisible(true);
                      setMobileActionSheet(false);
                    }}
                    className="w-full flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-touch"
                  >
                    <span className="font-medium text-[#2c1561]">Rename</span>
                  </button>
                )}

                {/* Move */}
                {(mobileActionItem.isFolder ||
                  !mobileActionItem.isBuildingBlock) && (
                  <button
                    onClick={async () => {
                      setSelectedTopic(mobileActionItem);
                      await getAllFolders();
                      setIsMoveModalVisible(true);
                      setMobileActionSheet(false);
                    }}
                    className="w-full flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-touch"
                  >
                    <span className="font-medium text-[#2c1561]">Move to</span>
                  </button>
                )}

                {/* Share */}
                {!mobileActionItem.isFolder &&
                  !mobileActionItem.isBuildingBlock && (
                    <button
                      onClick={() => {
                        setSelectedTopic(mobileActionItem);
                        setIsShareModalVisible(true);
                        setMobileActionSheet(false);
                      }}
                      className="w-full flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors min-h-touch"
                    >
                      <span className="font-medium text-[#2c1561]">Share</span>
                    </button>
                  )}

                {/* Delete */}
                <button
                  onClick={() => {
                    setSelectedTopic(mobileActionItem);
                    setIsDeleteModalVisible(true);
                    setMobileActionSheet(false);
                  }}
                  className="w-full flex items-center gap-3 p-4 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors min-h-touch"
                >
                  <span className="font-medium text-red-600">Delete</span>
                </button>
              </div>
            </BottomSheet>
          )}
        </div>
      )}
    </ConfigProvider>
  );
};

export const DocsList = ({
  newTab = false,
  height = null,
  style = {},
  filteredId = null
}: {
  newTab?: boolean;
  height?: string;
  style?: React.CSSProperties;
  filteredId?: string;
}) => {
  return (
    <DocsListInternal
      newTab={newTab}
      height={height}
      style={style}
      filteredId={filteredId}
    />
  );
};
